import { usePluginsHistory, useResetPlugins } from "@api/designAutomations";
import { useAppSettings, useUpdateAppSetting } from "@api/appSettings";
import i18n from "@i18n/i18n";
import { Button, Card, CardContent, Container, Grid, Switch, Typography, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { ChangeEvent, useEffect, useState } from "react";
import DesignAutomationGrid from "./DesignAutomationGrid";
import { AppSettingType } from "@api/apiClient";

const DesignAutomationPage = observer(() => {
  const theme = useTheme();
  const pluginHistory = usePluginsHistory();
  const resetPlugins = useResetPlugins();
  const [enableLogs, setEnableLogs] = useState(false);
  const [enableWebhooks, setEnableWebhooks] = useState(false);

  const appSettings = useAppSettings();
  const updateAppSetting = useUpdateAppSetting();

  useEffect(() => {
    const enableLogsValue = appSettings.data?.settings?.[AppSettingType.DesignAutomation_EnableLogs]?.value;
    if (enableLogsValue !== undefined) {
      setEnableLogs(enableLogsValue.toLowerCase() === "true");
    }
  }, [appSettings.data?.settings]);

  useEffect(() => {
    const enableWebhooksValue = appSettings.data?.settings?.[AppSettingType.DesignAutomation_EnableWebhooks]?.value;
    if (enableWebhooksValue !== undefined) {
      setEnableWebhooks(enableWebhooksValue.toLowerCase() === "true");
    }
  }, [appSettings.data?.settings]);

  const handleResetPlugins = async () => {
    await resetPlugins.mutateAsync();
    await pluginHistory.refetch();
  };

  const handleEnableLogsChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setEnableLogs(newValue);

    try {
      // UpdateAppSetting now handles upsert (create if doesn't exist, update if exists)
      await updateAppSetting.mutateAsync({
        key: AppSettingType.DesignAutomation_EnableLogs,
        value: newValue.toString(),
      });
      await appSettings.refetch();
    } catch (error) {
      console.error("Error updating DesignAutomation_EnableLogs setting:", error);
      // Revert the UI state on error
      setEnableLogs(!newValue);
    }
  };

  const handleEnableWebhooksChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setEnableWebhooks(newValue);

    try {
      // UpdateAppSetting now handles upsert (create if doesn't exist, update if exists)
      await updateAppSetting.mutateAsync({
        key: AppSettingType.DesignAutomation_EnableWebhooks,
        value: newValue.toString(),
      });
      await appSettings.refetch();
    } catch (error) {
      console.error("Error updating DesignAutomation_EnableWebhooks setting:", error);
      // Revert the UI state on error
      setEnableWebhooks(!newValue);
    }
  };

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Grid size={12}>
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("design-automation.settings.title")}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid>
                  <Typography variant="body1">
                    {i18n.t("design-automation.settings.enable-logs")}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color={theme.palette.grey[500]}
                    fontSize="0.75rem"
                  >
                    {i18n.t("design-automation.settings.enable-logs-description")}
                  </Typography>
                </Grid>
                <Grid>
                  <Switch
                    checked={enableLogs}
                    onChange={handleEnableLogsChange}
                    disabled={appSettings.isLoading || updateAppSetting.isPending}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <Card elevation={3}>
            <CardContent>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid>
                  <Typography variant="body1">
                    {i18n.t("design-automation.settings.enable-webhooks")}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color={theme.palette.grey[500]}
                    fontSize="0.75rem"
                  >
                    {i18n.t("design-automation.settings.enable-webhooks-description")}
                  </Typography>
                </Grid>
                <Grid>
                  <Switch
                    checked={enableWebhooks}
                    onChange={handleEnableWebhooksChange}
                    disabled={appSettings.isLoading || updateAppSetting.isPending}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <Button variant="contained" loading={resetPlugins.isPending} onClick={handleResetPlugins}>
            {i18n.t("design-automation.settings.reset-plugins")}
          </Button>
        </Grid>
        <Grid size={12} sx={{ display: "flex", flexGrow: 1, width: "100%", position: "relative" }}>
          <DesignAutomationGrid />
        </Grid>
      </Grid>
    </Container>
  );
});

export default DesignAutomationPage;
